import React, { useState, useRef, forwardRef, useEffect } from "react";
import { ContentItem } from "../types";
import { Plus } from "lucide-react";
import ProductParallaxTrial from "./productParallax/ProductParallaxTrial";
import iconTime from "../assets/images/icon-time.svg";
import iconLocation from "../assets/images/icon-location.svg";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import EditPunishment from "./EditPunishmentForm";
import { deleteDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db, getDefaultPunishmentsImages } from "../services/firebase";
import Drawer from "./ui/Drawer";
import CustomButtonGroup from "./ui/CustomButtonGroup";
import HoverVideoPlayer from "react-hover-video-player";
import { css, keyframes } from "@emotion/css";
import Markdown from "react-markdown";
import SocialMediaPreview from "./SocialMediaPreview";
import remarkGfm from 'remark-gfm';
import { IconInfo, IconShare } from "./CustomIcons";
import ShareThis from "./utils/shareThis";
//import defaultCardImage from "../assets/PunishmentDefault.png";

interface PunishmentReceipeProps {
  content: ContentItem;
  userRole: string | null;
  onVote: (voteType: "up" | "down") => void;
  upvotes: number;
  downvotes: number;
  userVote: "up" | "down" | null;
  nextContent: ContentItem | null;
  prevContent: ContentItem | null;
}

const PunishmentReceipe: React.FC<PunishmentReceipeProps> = ({
  content,
  userRole,
  onVote,
  upvotes,
  downvotes,
  userVote,
  nextContent,
  prevContent,
}) => {
  const navigate = useNavigate();

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1366 },
      items: 3,
      slidesToSlide: 3, // optional, default to 1.
    },
    tablet: {
      breakpoint: { max: 1365, min: 768 },
      items: 2,
      slidesToSlide: 2, // optional, default to 1.
    },
    mobile: {
      breakpoint: { max: 767, min: 0 },
      items: 1,
      slidesToSlide: 1, // optional, default to 1.
    },
  };

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [defaultCardImage, setDefaultCardImage] = useState<string>("");
  const [defaultCardVideo, setDefaultCardVideo] = useState<string>("");

  const ratingIcons = (count: number, filled: boolean = true) => (
    <div className="ratingBlk">
      {[...Array(count)].map((_, i) => (
        <span key={i} className="active">
          Rate
        </span>
      ))}
      {[...Array(5 - count)].map((_, i) => (
        <span key={i} className="">
          Rate
        </span>
      ))}
    </div>
  );

  // Parse the rating details
  const getRatingDetails = () => {
    if (!content.flRating) return { level: "", description: "" };
    const match = content.flRating.match(/(.+) Level (\d+)/);
    return {
      level: match ? match[2] : "",
      description: match ? match[1].trim() : "",
    };
  };

  const { level, description } = getRatingDetails();

  const handleNextPrev = (id: string) => {
    if (id && id !== "") {
      // navigate(`/punishments/${id}`);
      window.location.href = `/punishments/${id}`;
    }
  };

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const bannerHeight = document.getElementById("banner")?.offsetHeight || 0;
      const scrollY = window.scrollY;

      setIsSticky(scrollY > bannerHeight - 100); // Adjust this value as needed
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const fetchDefaultCardImage = async () => {
      const data = await getDefaultPunishmentsImages();
      if (data.length > 0) {
        setDefaultCardImage(data[0].recipeImageUrl || "");
        setDefaultCardVideo(data[0].recipeVideoUrl || "");
      } else {
        console.warn("No default punishment images found.");
      }
    };
    fetchDefaultCardImage();
  }, []);

  const loadedPlatforms = useRef<Set<string>>(new Set());
  const loadPlatformScript = (platform: string, url: string): (() => void) => {
    if (loadedPlatforms.current.has(platform)) {
      return () => {};
    }

    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.id = `${platform}-script`;
    document.body.appendChild(script);
    loadedPlatforms.current.add(platform);

    return () => {
      const existingScript = document.getElementById(`${platform}-script`);
      if (existingScript) {
        document.body.removeChild(existingScript);
        loadedPlatforms.current.delete(platform);
      }
    };
  };


  const handleDeletePunishment = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this punishment? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "content", content.id));
      navigate("/explore");
    } catch (error) {
      console.error("Error deleting punishment:", error);
      alert("Failed to delete punishment. Please try again.");
    }
  };

  // Create refs for each section
  const inspirationRef = useRef<HTMLDivElement>(null); // Specify the element type for the ref
  const summeryRef = useRef<HTMLDivElement>(null);
  const equiptmentRef = useRef<HTMLDivElement>(null);
  const instructionRef = useRef<HTMLDivElement>(null);

  // Handler function to scroll to an element
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth", // Enables smooth scrolling
        block: "start", // Aligns the top of the element to the top of the visible area
        // inline: 'nearest' // Optional: for horizontal scrolling
      });
    }
  };

  // State for read more functionality
  // State to manage read more/less functionality
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLParagraphElement | null>(null); // Ref to get the height for transition
  const [initialCharCount, setInitialCharCount] = useState(200);
  const [displayMobileInfo, setDisplayMobileInfo] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);

  useEffect(() => {
    if (content.imageDescription.length <= initialCharCount) {
      setDisplayedText(content.imageDescription);
      setShowButton(false);
    } else {
      setDisplayedText(
        isExpanded
          ? content.imageDescription
          : `${content.imageDescription.substring(0, initialCharCount)}...`
      );
      setShowButton(true);
    }
  }, [content.imageDescription, initialCharCount, isExpanded]);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const renderDescription = (description: string | "") => {
    switch (description) {
      case "Pee Wee":
        return "Basic";
      case "Two Hand Touch":
        return "Challenging";
      case "Full Contact":
        return "Serious";
      case "All Pro Elite":
        return "Ruthless";
      case "No Mercy":
        return "Insane";
      default:
        return null;
    }
  };

  // Keyframes for loading spinner animation
  const loadingOverlaySpinnerAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
  // Render the content
  const renderContent = () => (
    <>
      <div
        className={`punishmentInnerBanner${isSticky ? " bannerSticky" : ""}`}
        id="banner"
      >
        <div className="wrapper">
          <div className="bannerLeftCont">
            <h2>{content.title}</h2>
            <p>
              <Markdown remarkPlugins={[remarkGfm]}>{content.subtitle}</Markdown>
            </p>
            <ul className="bannerLabels">
              <li>
                <div className="labelHeading">Shame:</div>
                {ratingIcons(parseInt(content.embarrassmentLevel) || 0)}
              </li>
              <li>
                <div className="labelHeading">Danger:</div>
                {ratingIcons(parseInt(content.riskLevel) || 0)}
              </li>
              <li>
                <div className="labelHeading">Effort:</div>
                {ratingIcons(parseInt(content.longTermEffects) || 0)}
              </li>
              <li>
                <div className="labelHeading">Level {level}:</div>
                <div className="levelName">
                  {renderDescription(description)}
                </div>
              </li>
            </ul>
            <ShareThis
              title={content.title}
              description={content.subtitle}
              image={
                typeof content.image === "string" && content.image
                  ? content.image
                  : defaultCardImage
              }
            />
          </div>
          <div className="bannerRightCont">
            {
              /*https://www.npmjs.com/package/react-hover-video-player*/
              content.videoUrl ? (
                <HoverVideoPlayer
                  onClick={() => {setDisplayMobileInfo(false); setIsVideoPlaying(!isVideoPlaying)}}
                  pausedOverlayWrapperClassName="paused-overlay-wrapper" // Applies a custom class to the div wrapping the pausedOverlay contents
                  hoverOverlayWrapperClassName="hover-overlay-wrapper" // Applies a custom class to the div wrapping the hoverOverlay contents
                  videoClassName="player-video" // Applies a custom class to the video element
                  videoId="hover-video" // Applies an id to the video element
                  videoSrc={
                    content.IsdefaultVideo ? defaultCardVideo : content.videoUrl
                  }
                  videoStyle={{ borderRadius: "20px" }}
                  pausedOverlay={
                    <img
                      src={
                        typeof content.image === "string" && content.image
                          ? content.image
                          : defaultCardImage
                      }
                      alt={content.title}
                    />
                  }
                  loadingOverlayWrapperClassName={css`
                    /* Loading overlay has semi-transparent background so the player
                  will be darkened while it's visible */
                    background-color: rgba(0, 0, 0, 0.7);
                    /* Center the spinner inside of the overlay */
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  `}
                  loadingOverlay={
                    <div>
                      <div
                        className={css`
                          width: 4em;
                          height: 4em;
                          border: 6px solid white;
                          border-radius: 50%;
                          border-color: white white transparent transparent;
                          animation: ${loadingOverlaySpinnerAnimation} 1s linear
                            infinite;
                        `}
                      />
                    </div>
                  }
                  className="punishmentVideo"
                  loop={false}
                  unloadVideoOnPaused={false}
                  muted={true}
                  overlayTransitionDuration={500}
                  disablePictureInPicture={true}
                  disableRemotePlayback={true}
                  sizingMode="overlay"
                  preload="auto"
                  controls={true}
                  disableDefaultEventHandling={true}
                  focused={isVideoPlaying}
                  controlsList="nodownload"
                ></HoverVideoPlayer>
              ) : /* content.image && (
                  <img
                    src={
                      typeof content.image === "string"
                        ? content.image
                        : URL.createObjectURL(content.image)
                    }
                    alt={content.title}
                  />
                ) */
              content.image ? (
                <img
                  src={
                    typeof content.image === "string"
                      ? content.image
                      : defaultCardImage
                  }
                  alt={content.title}
                />
              ) : (
                <img src={defaultCardImage} alt="Default Punishment Image" />
              )
            }

            {content.imageCredit && (
              <span className="imgCredit">
                {
                  content.imageCreditLink ? (
                    <a href={content.imageCreditLink} target="_blank" rel="noopener noreferrer">
                      {content.imageCredit}
                    </a>
                  ) : content.imageCredit
                }
              </span>
            )}
            <div className="mobileBannerDetails">
              <div className={`levelBlk badge-${level}`}>
                Level {level}
              </div>
              <div className="mobileOtherInfo" onClick={() => setDisplayMobileInfo(!displayMobileInfo)}>
                <IconInfo />
                {displayMobileInfo &&
                  (
                  <ul className="bannerLabelsMobile">
                    <li>
                      <div className="labelHeading">Shame:</div>
                      {ratingIcons(parseInt(content.embarrassmentLevel) || 0)}
                    </li>
                    <li>
                      <div className="labelHeading">Danger:</div>
                      {ratingIcons(parseInt(content.riskLevel) || 0)}
                    </li>
                    <li>
                      <div className="labelHeading">Effort:</div>
                      {ratingIcons(parseInt(content.longTermEffects) || 0)}
                    </li>
                  </ul>
                  )
                }
                
              </div>
            </div>
            <div className="nextPrevMobile">
              {prevContent && (
                <span
                  className="prevOption"
                  onClick={() => {
                    handleNextPrev(prevContent.slug || "");
                  }}
                ></span>
              )}
              {nextContent && (
                <span
                  className="nextOption"
                  onClick={() => {
                    handleNextPrev(nextContent.slug || "");
                  }}
                ></span>
              )}
            </div>
          </div>
          <div className="nextPrevPunishment">
            {prevContent && (
              <span
                className="prevOption"
                onClick={() => {
                  handleNextPrev(prevContent.slug || "");
                }}
              >
                Previous Punishment
              </span>
            )}
            {nextContent && (
              <span
                className="nextOption"
                onClick={() => {
                  handleNextPrev(nextContent.slug || "");
                }}
              >
                Next Punishment
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="wrapper">
        <div className="innerTabNav">
          <ul>
            <li
              className={activeTab === 1 ? "active" : ""}
              onClick={() => {
                scrollToSection(inspirationRef);
                setActiveTab(1);
              }}
            >
              Inspiration
            </li>
            <li
              className={activeTab === 2 ? "active" : ""}
              onClick={() => {
                scrollToSection(summeryRef);
                setActiveTab(2);
              }}
            >
              Summary
            </li>
            {content.equipmentProducts &&
              content.equipmentProducts.length > 0 && (
                <li
                  className={activeTab === 3 ? "active" : ""}
                  onClick={() => {
                    scrollToSection(equiptmentRef);
                    setActiveTab(3);
                  }}
                >
                  Equipment
                </li>
              )}
            <li
              className={activeTab === 4 ? "active" : ""}
              onClick={() => {
                scrollToSection(instructionRef);
                setActiveTab(4);
              }}
            >
              Instructions
            </li>
          </ul>
        </div>
        <section ref={inspirationRef} style={{ scrollMarginTop: "200px" }}>
          <SocialMediaPreview
            links={content.socialMediaLinks || []}
            isEditing={false}
            contentId={content.id}
            contentTitle={content.title}
            contentType="punishment"
          />
          
        </section>
        <section ref={summeryRef} style={{ scrollMarginTop: "200px" }}>
          <h3>Summary</h3>
          <p ref={contentRef}>
            <Markdown remarkPlugins={[remarkGfm]}>{displayedText}</Markdown>
          </p>
          {showButton && (
            <p>
              <a onClick={toggleReadMore}>
                {isExpanded ? "Read Less" : "Read More"}
              </a>
            </p>
          )}

          <ul className="summaryList">
            <li>
              <div className="listHeading">
                <img src={iconTime} alt="" />
                <span>Time to Complete:</span>
              </div>
              <div className="listContent">
                <p>{content.timeToComplete}</p>
                <div className="subTxt">{content.timeToCompleteSubHeading}</div>
              </div>
            </li>
            <li>
              <div className="listHeading">
                <img src={iconTime} alt="" />
                <span>Cost to Complete:</span>
              </div>
              <div className="listContent">
                <p>{content.costToComplete}</p>
                <div className="subTxt">{content.costToCompleteSubHeading}</div>
              </div>
            </li>
            <li>
              <div className="listHeading">
                <img src={iconLocation} alt="" />
                <span>Punishment Location:</span>
              </div>
              <div className="listContent">
                <p>{content.equipmentLocker}</p>
                <div className="subTxt">
                  {content.equipmentLockerSubHeading}
                </div>
              </div>
            </li>
          </ul>
        </section>
        {content.equipmentProducts && content.equipmentProducts.length > 0 && (
          <section
            className="ourShop innerPageShop"
            ref={equiptmentRef}
            style={{ scrollMarginTop: "-50px" }}
          >
            <ProductParallaxTrial
              equipmentProducts={content.equipmentProducts || []}
              equipmentImage={content.equipmentImageLink || ""}
            />
          </section>
        )}
        <section ref={instructionRef} style={{ scrollMarginTop: "200px" }}>
          <h3>Instructions</h3>
          <ul className="stepList">
            {content.instructions.map((instruction, index) => (
              <li key={index}>
                <div className="tagItem">Step {index + 1}</div>
                <ul>
                  <li><Markdown remarkPlugins={[remarkGfm]}>{instruction.description}</Markdown></li>
                </ul>
              </li>
            ))}
            <li>
              <div className="tagItem alertItem">Ways to Spice it Up</div>
              <ul>
                {content.waysToSpiceUp?.length > 1
                  ? content.waysToSpiceUp.map((spice, index) => (
                      <li key={index}>{spice}</li>
                    ))
                  : content.waysToSpiceUp.map((spice, index) => (
                      <div>
                      <Markdown remarkPlugins={[remarkGfm]}>{spice}</Markdown>
                      </div>
                    ))}
              </ul>
            </li>
          </ul>
        </section>
        {!isEditing && userRole === "admin" && (
          <div className="adminActionWrap">
            <button
              onClick={() => setIsEditing(true)}
              className="adminActionBtn"
            >
              Edit
            </button>
            <button
              onClick={handleDeletePunishment}
              className="adminActionBtn actionDanger"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </>
  );

  useEffect(() => {
    const sectionRefs = [
      { ref: inspirationRef, tab: 1 },
      { ref: summeryRef, tab: 2 },
      { ref: equiptmentRef, tab: 3 },
      { ref: instructionRef, tab: 4 },
    ];

    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -50% 0px", // adjust to trigger when section passes top 40%
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          console.log("Intersecting section:", entry.target);
          const matched = sectionRefs.find(
            (s) => s.ref.current === entry.target
          );
          if (matched) {
            setActiveTab(matched.tab);
          }
        }
      });
    }, observerOptions);

    // Observe each section
    sectionRefs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Cleanup on unmount
    return () => {
      sectionRefs.forEach(({ ref }) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  return (
    <>
      {isEditing && userRole === "admin" ? (
        <EditPunishment
          content={content}
          cancelEditing={(data) => {
            setIsEditing(!data);
          }}
        />
      ) : (
        renderContent()
      )}
    </>
  );
};
export default PunishmentReceipe;


